import React, { useState } from "react";
import '../concept-mapper.css';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import { styled } from '@mui/material/styles';
import FileUploadIcon from '@mui/icons-material/FileUpload';
import DownloadIcon from '@mui/icons-material/Download';
import styles from './ConceptMapperPage.module.scss';
import Alert from '@mui/material/Alert';
import CircularProgress from '@mui/material/CircularProgress';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import LinearProgress from '@mui/material/LinearProgress';

const VisuallyHiddenInput = styled('input')({
  clip: 'rect(0 0 0 0)',
  clipPath: 'inset(50%)',
  height: 1,
  overflow: 'hidden',
  position: 'absolute',
  bottom: 0,
  left: 0,
  whiteSpace: 'nowrap',
  width: 1,
});

function ConceptMapperForm() {
  const [toggle, setToggle] = useState(false);
  const [results, setResults] = useState([]);
  const [status, setStatus] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [progress, setProgress] = useState(0);
  const [selectedModels, setSelectedModels] = useState({});
  const [canToggle, setCanToggle] = useState(false);
  const [csvHeaders, setCsvHeaders] = useState([]); 
  const [formData, setFormData] = useState({
    domain: "",
    vocabulary: "",
    conceptClass: "",
    conceptColumn: "",
    conceptCsv: null,
    modelPack: null,
  });
  const [sortAsc, setSortAsc] = useState(true);
  const [displayResults, setDisplayResults] = useState([]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prevData) => ({
      ...prevData,
      [name]: value,
    }));
  };

  const handleSort = () => {
    const sorted = [...displayResults].sort((a, b) => {
      return sortAsc
        ? a.searchPhrase.toLowerCase().localeCompare(b.searchPhrase.toLowerCase())
        : b.searchPhrase.toLowerCase().localeCompare(a.searchPhrase.toLowerCase());
    });
    setDisplayResults(sorted);
    setSortAsc(prev => !prev);
  };

  const handleDownload = () => {
    let csvContent = "searchPhrase,selectedConcept\n";

    displayResults.forEach((row) => {
      const phrase = row.searchPhrase;
      const selectedModel = selectedModels[phrase] || row.defaultModel;
      const conceptName = row.models[selectedModel]?.concept
      csvContent += `${phrase},${conceptName}\n`;
    });

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.setAttribute("href", url);
    link.setAttribute("download", "selected_concepts.csv");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleClear = () => {
    setFormData({
      domain: '',
      vocabulary: '',
      conceptClass: '',
      conceptColumn: '',
      conceptCsv: null,
      modelPack: null,
    });

    setCsvHeaders([]);
  };

  const handleFileChange = (e) => {
    const { name, files } = e.target;
    const file = files[0];

    if (file && name === "conceptCsv") {
      const reader = new FileReader();
      reader.onload = (event) => {
        const text = event.target.result;
        const lines = text.split(/\r?\n/);
        const headers = lines[0].split(',').map(h => h.trim());
        setCsvHeaders(headers);
      };
      reader.readAsText(file);
    }

    setFormData((prevData) => ({
      ...prevData,
      [name]: file,
    }));
  };

  const handleSelectChange = (phrase, selectedModel) => {
    setSelectedModels((prev) => ({
      ...prev,
      [phrase]: selectedModel,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    setTimeout(() => {
    setToggle(prev => !prev);
    }, 0);

    setCanToggle(true);
    
    setStatus("🛠️ Starting Concept Mapper... This may take a few minutes.");
    setResults([]);
    setError('');
    setSelectedModels({});
    setLoading(true);

    
    const form = new FormData();
    Object.entries(formData).forEach(([key, value]) => form.append(key, value));

    try {
      const response = await fetch("/api/conceptMapper/runMapper", {
        method: "POST",
        body: form,
      });

      if (!response.ok) {
        const error = await response.json();
        setStatus(`❌ Error: ${error.error}`);
        return;
      }

      const task = await response.json();
      const task_id = task.id;


      const checkStatus = async () => {
        const res = await fetch(`/api/conceptMapper/status/${task_id}`);
        const data = await res.json();

        if (data.error) {
          setStatus(`❌ Failed: ${data.error}`);
        } else if (data.done) {
          setStatus("✅ Concept mapping complete! Fetching results...");
          console.log(res);

          const resultRes = await fetch(`/api/conceptMapper/results/${task_id}`);
          const resultData = await resultRes.json();
           
            // Log the status response from the status API
            console.log("Status from status API:", data);

          if (Array.isArray(resultData)) {
            setResults(resultData);
            setSortAsc(true);
            setDisplayResults(resultData);
            const initialSelections = {};
            resultData.forEach((row) => {
              initialSelections[row.searchPhrase] = row.defaultModel;
            });
            setSelectedModels(initialSelections);
            setStatus("🎉 Mapping completed. Results loaded.");
            setLoading(false);
            setProgress(0);
          } else {
            setStatus(`⚠️ Error loading results: ${resultData.error}`);
          }
        } else if (data.processing) {
          const current = data.current || 0;
          const total = data.total || 0;
          const percent = data.percent ?? -1;

          if (percent === -1) {
            setStatus("🛠️ Preparing models please wait...");
            setProgress(0);
          } else {
            setStatus(`⏳ Processing: ${percent}% (${current}/${total})`);
            setProgress(percent);
          }

          setTimeout(checkStatus, 1000);
        } else {
          setStatus("⚠️ Unexpected status.");
        }
      };

      setTimeout(checkStatus, 5000);
    } catch (err) {
      console.error(err);
      setStatus("❌ An error occurred while calling the server.");
    }
  };

  return (
    <div className={styles.wrapper}>
      <div className={styles.inputs}>
        {(results.length !== 0 || canToggle) && (
          <button
            className={styles.inputs__toggle_button}
            onClick={() => setToggle(!toggle)}
            // variant="contained"
          >
            {toggle ? <ChevronRightIcon /> : <ChevronLeftIcon />}
          </button>
        )}

        {toggle ? (
          <h2 className={styles.inputs__closed__heading}>Inputs</h2>
        ) : (
          <>
            <h2>Inputs</h2>
            <form className={styles.inputs__form} onSubmit={handleSubmit}>
              <div className={styles.inputs__text_inputs}>
                <TextField
                  id="outlined-basic"
                  label="Domain ID"
                  variant="outlined"
                  name="domain"
                  value={formData.domain}
                  onChange={handleChange}
                  required
                />
                <TextField
                  id="outlined-select"
                  select
                  label="Vocabulary ID"
                  //variant="outlined"
                  name="vocabulary"
                  value={formData.vocabulary}
                  onChange={handleChange}
                  required
                  SelectProps={{ native: true }}
                >
                  <option value="" disabled ></option>
                  <option value="SNOMED">SNOMED</option>
                </TextField>
                <TextField
                  id="outlined-basic"
                  label="Concept class ID"
                  variant="outlined"
                  name="conceptClass"
                  value={formData.conceptClass}
                  onChange={handleChange}
                  required
                />
                {csvHeaders.length > 0 ? (
                <TextField
                  id="outlined-select"
                  select
                  label="Concept name column"
                  name="conceptColumn"
                  value={formData.conceptColumn}
                  onChange={handleChange}
                  required
                  SelectProps={{ native: true }}
                >
                  <option value="" disabled></option>
                  {csvHeaders.map((header, idx) => (
                    <option key={idx} value={header}>
                      {header}
                    </option>
                  ))}
                </TextField>
              ) : (
                <TextField
                  id="outlined-basic"
                  label="Upload File Below First"
                  variant="outlined"
                  name="conceptColumn"
                  value={formData.conceptColumn}
                  onChange={handleChange}
                  required
                  disabled
                />
              )}
              </div>
              <div className={styles.inputs__file_inputs}>
                <div className={styles.input__file_upload}>
                  <Button
                    component="label"
                    role={undefined}
                    variant="contained"
                    tabIndex={-1}
                    startIcon={<FileUploadIcon />}
                  >
                    Upload file
                    <VisuallyHiddenInput
                      type="file"
                      name="conceptCsv"
                      onChange={handleFileChange}
                      
                    />
                  </Button>
                  {formData.conceptCsv ? (
                    <span className={styles.input__file_upload__description}>
                      {formData.conceptCsv.name.substring(0, 30) + '...'}
                    </span>
                  ) : (
                    <span
                      className={
                        styles.input__file_upload__description__placeholder
                      }
                    >
                      Concepts to map
                    </span>
                  )}
                </div>
              </div>
              <div className={styles.inputs__end_buttons}>
                <Button variant="contained" onClick={handleClear}>
                  Clear
                </Button>
                <Button variant="contained" type="submit" disabled={!formData.domain || !formData.vocabulary || !formData.conceptClass || !formData.conceptColumn || !formData.conceptCsv}>
                  Run Concept Mapper
                </Button>
              </div>
            </form>
          </>
        )}
      </div>
        <div className={styles.results}>
        <div className={styles.results__heading__container}></div>
        <h2>Results</h2>
    <div className={styles.results}>
          <div className={styles.results__heading__container}>
            {results.length !== 0 && (
              <>
              <Button
                variant="contained"
                onClick={handleDownload}
                startIcon={<DownloadIcon />}
              >
                Download results
              </Button>
              <table style={{width:'20%'}}>
                <thead>
                  <tr>Confidence</tr>
                  <tr className="confidence-high">High</tr>
                  <tr className="confidence-medium">Medium</tr>
                  <tr className="confidence-low">Low</tr>
                </thead>
              </table>
              </>
            )}
            </div>
            {loading && (
            <div className={styles.results__loading}>
              <div className={styles.results__loading__text__container}>
                <p className={styles.results__loading__text}>
                  {status}
                </p>
                {progress === 0 ? (
                  <CircularProgress />
                ) : (
                  <LinearProgress
                    variant="determinate"
                    value={progress}
                    style={{ width: '100%', marginTop: '10px' }}
                  />
                )}
              </div>
            </div>
          )}
          {error && !loading && <Alert severity="error">{error}</Alert>}
          {results.length !== 0 && (
          <>
            <div className={styles.results__sort_button_container}>
                <Button variant="contained" onClick={handleSort}>
                  Sort: {sortAsc ? "A → Z" : "Z → A"}
                </Button>
          </div>
            <div className={styles.results__table__container}>
              <table className={styles.results__table}>
                <thead>
                  <tr>
                    <th className={styles.results__table__header}>Search Phrase</th>
                    <th className={styles.results__table__header}>Fuzzy Concept</th>
                    <th className={styles.results__table__header}>MedCat Concept</th>
                    <th className={styles.results__table__header}>Reverse Index Concept</th>
                    <th className={styles.results__table__header}>Selected</th>
                  </tr>
                </thead>
                <tbody>
                  {displayResults.map((row, i) => {
                    const phrase = row.searchPhrase;
                    const selectedModel = selectedModels[phrase] || row.defaultModel;
                    const modelData = row.models[selectedModel];

                    return (
                      <tr
                        key={i}
                        className={
                          modelData.confidence === "High"
                            ? "confidence-high"
                            : modelData.confidence === "Medium"
                            ? "confidence-medium"
                            : "confidence-low"
                        }
                      >
                        <td className={styles.results__table__data}>{phrase}</td>
                        {Object.entries(row.models).map(([model, data]) => (
                          <td className={styles.results__table__data} key={model}>
                            {data.confidence !== "High" ? (
                              data.concept !== null ? (
                                <>
                                  <label
                                    htmlFor={`model-select-${phrase}-${model}`}
                                    style={{ display: "block", width: "100%", height: "100%", cursor: "pointer" }}
                                  >
                                    <input
                                      type="radio"
                                      name={`model-select-${phrase}`} // one group per row
                                      id={`model-select-${phrase}-${model}`}
                                      value={model}
                                      checked={selectedModel === model}
                                      onChange={() => handleSelectChange(phrase, model)}
                                      style={{ marginRight: "8px" }}
                                    />
                                    {data.concept}
                                  </label>
                                </>
                              ) : (
                                <p>N/A</p>
                              )
                            ) : (
                              data.concept ?? <p>N/A</p>
                            )}
                          </td>
                        ))}
                        <td className={styles.results__table__data}>{modelData.concept}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            </>
          )}
          </div>
        </div>
  </div>
  )}

export default ConceptMapperForm;
